import { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { ApiError } from '../../utils/api-error';
import { apiPublic } from '../../utils/data-utils';
import EmailFormQuestion from './EmailFormQuestion';
import { type LinkState, visibleQuestions } from './types';

function EmailForm({
  tenant,
  formName,
  token,
}: {
  tenant: string;
  formName: string;
  token: string;
}) {
  const [link, setLink] = useState<LinkState>();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const message = useRef<HTMLDivElement>(null);
  const methods = useForm<Record<string, unknown>>({ shouldUnregister: true });
  const values = methods.watch();
  const path = `/tenant-email-form/${encodeURIComponent(tenant)}/${encodeURIComponent(formName)}/${encodeURIComponent(token)}`;
  const detail = (cause: unknown) =>
    cause instanceof ApiError
      ? cause.detail || cause.message
      : 'Unable to save your response. Please try again.';

  useEffect(() => {
    let cancelled = false;
    apiPublic(path, { cache: 'no-store' })
      .then((response) => response.json())
      .then((body: LinkState) => {
        if (!cancelled) {
          methods.reset({});
          setLink(body);
        }
      })
      .catch((cause) => {
        if (!cancelled) setError(detail(cause));
      });
    return () => {
      cancelled = true;
    };
  }, [path]);

  useEffect(() => {
    if (error || submitted) message.current?.focus();
  }, [error, submitted]);

  const submit = methods.handleSubmit(
    async (answers) => {
      if (!link || saving) return;
      if (link.status === 'expired' && !email.trim()) {
        setError('Enter your email address.');
        return;
      }
      setSaving(true);
      setError('');
      const allowed = visibleQuestions(link.config, answers).filter(
        (q) => q.questionType !== 'content',
      );
      const selected = Object.fromEntries(
        allowed.filter((q) => answers[q.name] !== undefined).map((q) => [q.name, answers[q.name]]),
      );
      try {
        const response = await apiPublic(path, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            answers: selected,
            formVersion: link.config.version,
            ...(link.status === 'expired' ? { email: email.trim() } : {}),
          }),
        });
        const body = await response.json();
        setLink({ ...link, status: body.status });
        setSubmitted(body.status === 'submitted');
      } catch (cause) {
        setError(detail(cause));
        try {
          const response = await apiPublic(path, { cache: 'no-store' });
          const refreshed: LinkState = await response.json();
          setLink(refreshed);
        } catch {
          // preserve submission error if status refresh fails
        }
      } finally {
        setSaving(false);
      }
    },
    () => setError('Check the answers below.'),
  );

  const visible = link
    ? new Set(visibleQuestions(link.config, values).map((q) => q.name))
    : new Set();
  return (
    <main className="govuk-width-container govuk-main-wrapper">
      <div className="govuk-grid-row">
        <div
          className="govuk-grid-column-two-thirds"
          style={submitted ? { float: 'none', marginInline: 'auto' } : undefined}
        >
          <h1 className="govuk-heading-l">{link?.config.title || 'Email form'}</h1>
          {!submitted && link?.config.hint && <p className="govuk-hint">{link.config.hint}</p>}
          {error && (
            <div className="govuk-error-summary" role="alert" tabIndex={-1} ref={message}>
              {error}
            </div>
          )}
          {submitted && link ? (
            <div
              className="govuk-panel govuk-panel--confirmation"
              tabIndex={-1}
              ref={message}
              role="status"
            >
              <h2 className="govuk-panel__title govuk-!-font-size-36">
                {link.config.confirmationTitle}
              </h2>
              <div className="govuk-panel__body govuk-!-font-size-27">
                {link.config.confirmationText}
              </div>
            </div>
          ) : link ? (
            <FormProvider {...methods}>
              <form onSubmit={submit}>
                {link.status === 'expired' && (
                  <div className="govuk-form-group">
                    <p className="govuk-body">
                      Your original link details have expired. Please enter your email address with
                      your response.
                    </p>
                    <label className="govuk-label" htmlFor="email-form-recipient">
                      Email address
                    </label>
                    <input
                      className="govuk-input"
                      id="email-form-recipient"
                      type="email"
                      autoComplete="email"
                      required
                      maxLength={254}
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                    />
                  </div>
                )}
                {link.config.pages.map((page) => (
                  <section key={page.name}>
                    {page.title && <h2 className="govuk-heading-m">{page.title}</h2>}
                    {page.questions
                      .filter((q) => visible.has(q.name))
                      .map((question) => (
                        <EmailFormQuestion key={question.name} question={question} />
                      ))}
                  </section>
                ))}
                <button className="govuk-button" type="submit" disabled={saving}>
                  {saving ? 'Submitting…' : link.config.submitText}
                </button>
              </form>
            </FormProvider>
          ) : (
            !error && <p className="govuk-body">Loading…</p>
          )}
        </div>
      </div>
    </main>
  );
}

export default function TenantEmailForm() {
  const { tenant = '', formName = 'out-of-scope', responseId, declarationId } = useParams();
  const token = responseId || declarationId || '';
  return (
    <EmailForm
      key={`${tenant}/${formName}/${token}`}
      tenant={tenant}
      formName={formName}
      token={token}
    />
  );
}
